import React, { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { permissionMapApi } from '../../services/api';
import { GridSection } from './permissionMap';

/**
 * The access matrix (GET /api/admin/permission-map/matrix): every navigation row against every
 * action, from the checks that enforce them. Without a person each cell says what it takes; with one
 * each cell is their answer and, for a no, every key they lack. Rows are the sidebar's, in its order;
 * a data entry module lists the tables it writes under it.
 *
 * `sources` (person view) maps a key to its resolved { access, source }, so a missing key that is
 * denied rather than never granted says so. `onlyNo` keeps only the rows with a refusal (the caller
 * owns the checkbox). Double-click a line for its gates, in order.
 */
const ACTIONS = [['save', 'Save'], ['delete', 'Delete'], ['datasheet', 'Datasheet save'], ['bulkDelete', 'Bulk delete']];
const CELLS = ['opens', 'sidebar', 'read', ...ACTIONS.map(([k]) => k), 'download'];
const KEY_COLUMNS = [
  { label: 'Page', width: '17%' }, { label: 'Opens', width: '15%' }, { label: 'In sidebar', width: '11%' },
  { label: 'Table', width: '11%' }, { label: 'Read', width: '13%' },
  ...ACTIONS.map(([, label]) => ({ label, width: '5%' })), { label: 'Download', width: '13%' },
];
const ANSWER = {
  need: 'Takes keys', available: 'Yes', open: 'No key', info: 'Rule', sysadmin: 'SysAdmin only',
  varies: 'By table', yes: 'Yes', no: 'No', na: 'N/A',
};
const PERSON_COLUMNS = [
  { label: 'Page', width: '18%' }, { label: 'Opens', width: '6%' }, { label: 'In sidebar', width: '7%' },
  { label: 'Table', width: '11%' }, { label: 'Read', width: '6%' },
  ...ACTIONS.map(([, label]) => ({ label, width: '6%' })), { label: 'Download', width: '7%' }, { label: 'They need' },
];

const DENIED = { GlobalDeny: 'denied globally', UserOverride: 'denied by an override' };

const keyList = (cell, sources) => {
  const parts = cell.keys.map(k => (sources?.[k] && DENIED[sources[k].source] ? `${k} (${DENIED[sources[k].source]})` : k));
  return parts.join(cell.anyOf ? ' or ' : ', ');
};

/** One cell as { value, className, title } for GridSection. */
const cellSpec = (cell, sources) => {
  const keys = keyList(cell, sources);
  const title = [keys, cell.note].filter(Boolean).join(' — ') || undefined;
  switch (cell.state) {
    case 'need': return { value: <code>{keys}</code>, className: 'stui-truncate', title };
    case 'available': return { value: 'Yes', title: keys ? `Needs ${keys}` : undefined };
    case 'open': return { value: 'No key', className: 'stui-text-muted', title: cell.note };
    case 'info': return { value: cell.note, className: 'stui-truncate', title: cell.note };
    case 'sysadmin': return { value: 'SysAdmin only', className: 'stui-truncate', title };
    case 'varies': return { value: 'By table', className: 'stui-truncate', title: cell.note };
    case 'yes': return { value: 'Yes', className: 'stui-access-grant', title: cell.note };
    case 'no': return { value: 'No', className: 'stui-access-deny', title };
    default: return { value: 'N/A', className: 'stui-text-muted', title: cell.note };
  }
};

const isNo = l => CELLS.some(c => c !== 'sidebar' && l[c].state === 'no');

/** Everything a line refuses for want of, once: the missing keys, then the reasons no grant fixes. */
const needs = (l, sources) => {
  const keys = [];
  const why = [];
  CELLS.filter(c => c !== 'sidebar' && l[c].state === 'no').forEach(c => {
    const cell = l[c];
    if (cell.keys.length) {
      const each = cell.anyOf ? [keyList(cell, sources)] : cell.keys.map(k => keyList({ keys: [k] }, sources));
      each.forEach(k => { if (!keys.includes(k)) keys.push(k); });
    } else if (cell.note && !why.includes(cell.note)) why.push(cell.note);
  });
  return [...keys, ...why].join('; ');
};

/** The action keys each surface asks for, stated once. */
export const SurfaceKeysGrid = ({ surfaceKeys }) => (
  <GridSection title="Action keys, by the page they are used from"
    columns={[{ label: 'Pages', width: '16%' }, ...ACTIONS.map(([, label]) => ({ label }))]}
    rows={surfaceKeys.map(s => ({
      key: s.surface,
      cells: [s.surface, ...ACTIONS.map(([k]) => (k === 'datasheet'
        ? <><code>{s.save}</code> + <code>{s.datasheet}</code></>
        : <code>{s[k]}</code>))],
    }))} />
);

const LineDetail = ({ line, sources, person, onClose }) => (
  <div className="stui-modal-overlay">
    <div className="stui-modal stui-modal-lg">
      <div className="stui-modal-header">
        <h3>{line.label}{line.isTable ? '' : ` (${line.navType})`}</h3>
        <button className="stui-modal-close" onClick={onClose}><X size={20} /></button>
      </div>
      <div className="stui-modal-body">
        <GridSection title="Cells"
          columns={[{ label: 'Column', width: '18%' }, { label: 'Answer', width: '22%' }, { label: 'Keys' }, { label: 'Why' }]}
          rows={[['opens', 'Opens'], ['sidebar', 'In sidebar'], ['read', 'Read'], ...ACTIONS, ['download', 'Download']]
            .filter(([k]) => line[k].state !== 'na' || line[k].note)
            .map(([k, label]) => {
              const spec = cellSpec(line[k], sources);
              return { key: k, cells: [label, { value: ANSWER[line[k].state] || line[k].state, className: spec.className }, keyList(line[k], sources), line[k].note || ''] };
            })} />
        {line.trails.map(t => (
          <GridSection key={t.action} title={`${t.action} on ${line.table}: the gates, in order`}
            columns={[{ label: '#', width: '6%' }, { label: 'Gate', width: '18%' }, { label: 'Checked', width: '34%' }, { label: 'Result' }]}
            rows={t.decision.trail.map((g, i) => ({
              key: i, inactive: !g.passed && (person || !g.isKey),
              // The key view walks as someone holding no keys: a key gate there is what the action takes.
              cells: [i + 1, g.gate, g.detail, g.passed ? 'Passed' : !person && g.isKey ? 'Takes this key' : (g.why || 'Failed')],
            }))} />
        ))}
        {line.trails.length === 0 && <p className="stui-hint stui-mt-md">This line saves nothing from its own list, so there are no gates to walk.</p>}
      </div>
      <div className="stui-modal-footer">
        <button className="stui-btn stui-btn-primary" onClick={onClose}>Close</button>
      </div>
    </div>
  </div>
);

const AccessMatrix = ({ matrix, sources, height, title, className, onlyNo = false }) => {
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const person = !!matrix.person;

  const lines = useMemo(() => {
    if (!onlyNo) return matrix.lines;
    // Keep each refused line's row, so a module's table lines stay under their module.
    const keep = new Set(matrix.lines.filter(isNo).map(l => l.navKey));
    return matrix.lines.filter(l => keep.has(l.navKey));
  }, [matrix, onlyNo]);

  const id = l => `${l.navKey}|${l.table || ''}|${l.isTable}`;

  return (
    <>
      <GridSection title={title || `Pages (${lines.length} lines)`} height={height} className={className}
        empty={onlyNo ? 'Nothing is refused.' : 'No rows.'}
        columns={person ? PERSON_COLUMNS : KEY_COLUMNS}
        rows={lines.map(l => ({
          key: id(l),
          selected: selected === id(l),
          inactive: person ? l.opens.state === 'no' && !l.isTable : false,
          onClick: () => setSelected(id(l)),
          onDoubleClick: () => { setSelected(id(l)); setDetail(l); },
          cells: [
            { value: <span className="stui-tree-indent" style={{ '--tree-depth': l.depth }}>{l.isTable ? '' : l.label}</span>, className: 'stui-truncate', title: l.isTable ? undefined : l.label },
            cellSpec(l.opens, sources), cellSpec(l.sidebar, sources),
            { value: l.table || '', className: 'stui-truncate', title: l.table || undefined },
            cellSpec(l.read, sources),
            ...ACTIONS.map(([k]) => cellSpec(l[k], sources)),
            cellSpec(l.download, sources),
            ...(person ? [(() => { const n = needs(l, sources); return { value: n, className: 'stui-truncate', title: n || undefined }; })()] : []),
          ],
        }))} />
      {detail && <LineDetail line={detail} sources={sources} person={person} onClose={() => setDetail(null)} />}
    </>
  );
};

/**
 * The matrix from the server: the key view with no arguments, one person on one database with both.
 * Fetched whenever they change, so a grant saved elsewhere shows the next time it loads.
 */
export const useAccessMatrix = (userId, databaseId, refresh = 0) => {
  const [matrix, setMatrix] = useState(null);
  const [error, setError] = useState(null);
  const wanted = userId == null || databaseId != null;
  useEffect(() => {
    setMatrix(null);
    setError(null);
    if (!wanted) return undefined;
    let cancelled = false;
    permissionMapApi.matrix(userId ?? undefined, databaseId ?? undefined)
      .then(res => { if (!cancelled) setMatrix(res.data); })
      .catch(err => { if (!cancelled) setError(err.response?.data?.error || 'Could not build the access matrix'); });
    return () => { cancelled = true; };
  }, [userId, databaseId, wanted, refresh]);
  return { matrix, error };
};

/** Permissions > Matrix: what every cell takes, for nobody in particular. The page grid fills the tab. */
export const KeyMatrixTab = ({ refresh }) => {
  const { matrix, error } = useAccessMatrix(null, null, refresh);
  return (
    <div className="stui-tab-body" style={{ display: 'flex', flexDirection: 'column' }}>
      {error && <div className="stui-alert stui-alert-error">{error}</div>}
      {!matrix && !error && <p className="stui-hint">Reading what the API enforces...</p>}
      {matrix && (
        <>
          <p className="stui-hint">
            Opens and Read name the keys they take. An action says Yes when the page offers it; it takes the
            action key below for that kind of page, on top of Opens and Read. A person's answers are on
            Users: pick the person, a database role, then the eye button.
          </p>
          <SurfaceKeysGrid surfaceKeys={matrix.surfaceKeys} />
          <AccessMatrix matrix={matrix} className="stui-flex-1" title={`Every page (${matrix.lines.length} lines; double-click for the gates)`} />
        </>
      )}
    </div>
  );
};

export default AccessMatrix;
